import React, {useState} from "react"
import {AsyncResultModal} from "../../components/AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../services/auth"
import {toCents} from "../../utilities/numbers"
import {AccountHold, partialReleaseAccountHold} from "../../resources/accountHold"

interface ReleaseAccountHoldModalProps {
	accountHold: AccountHold
	closeModal: () => void
	onSuccess: () => void
}

function refreshAndClose(refresh: () => void, close: () => void) {
	return function () {
		refresh()
		close()
	}
}

export function ReleaseAccountHoldModal({accountHold, closeModal, onSuccess}: ReleaseAccountHoldModalProps) {
	const accessToken = useAccessToken()
	const [releasedAmount, setReleasedAmount] = useState("")
	const [releaseHold, setReleaseHold] = useAsyncResultIdle(partialReleaseAccountHold)

	const holdAmount = (accountHold.attributes.remainingHoldAmount ?? accountHold.attributes.amount) / 100
	const numericReleasedAmount = parseFloat(releasedAmount) || 0
	const isOverHoldAmount = numericReleasedAmount > holdAmount
	const isValidAmount = !isNaN(numericReleasedAmount) && numericReleasedAmount > 0 && !isOverHoldAmount

	return (
		<AsyncResultModal
			title="Release Balance Hold"
			close={refreshAndClose(onSuccess, closeModal)}
			buttonClassname="is-dark"
			state={releaseHold}
			buttonText="Release Hold"
			successText="Released"
			onSubmit={() =>
				setReleaseHold(accessToken, accountHold.relationships.account.data.id, accountHold.id, toCents(releasedAmount))
			}
			errorToText={(err) => err.errors[0].title}
			buttonDisabled={!isValidAmount}
		>
			<div className="release-account-hold-modal">
				<p className="modal-description">How much would you like to release from the amount hold?</p>

				<div className="form-row two">
					<div className="field">
						<label className="label">Released Amount-</label>
						<div className="control">
							<div className="input-wrapper">
								<span className="currency-symbol">$</span>
								<input
									className={`input currency-input ${isOverHoldAmount ? "is-danger" : ""}`}
									type="number"
									min="0"
									step="0.01"
									value={releasedAmount}
									onChange={(e) => setReleasedAmount(e.target.value)}
									placeholder="500"
								/>
							</div>
							{isOverHoldAmount && <p className="help is-danger">Amount is higher than hold amount</p>}
						</div>
					</div>

					<div className="field">
						<label className="label">Hold Amount</label>
						<div className="control">
							<input className="input" type="text" value={`$${holdAmount.toFixed(2)}`} disabled />
						</div>
					</div>
				</div>

				<div className="disclaimer">
					<p>
						By selecting the &quot;Release Hold&quot; button, you acknowledge and agree that releasing this hold will
						make the held amount available for spending. This amount will no longer be reserved and cannot be guaranteed
						for any future transactions or obligations
					</p>
				</div>
			</div>
		</AsyncResultModal>
	)
}
